import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { getVehicleFormKey } from '@/components/App/NewOrder/helpers/helpers';
import useAutoCredRequestId from '@/components/App/NewOrder/hooks/useAutoCredRequestId';
import { type BankNames } from '@/constants';
import { handleError } from '@/services/handleError';
import { debounce } from '@/services/helpers/debounce';
import { useAppDispatch, useAppSelector } from '@/store';
import { setIsVehicleInfoValid } from '@/store/financingStep.slice';

import { getVisibleVehicleInfoFields } from '../../../helpers';
import {
  filterVehicleFieldsByVisibility,
  getVehicleInfoFormValues
} from '../../../helpers/vehicleInfo';
import type { VehicleFormInterface } from '../types/vehicle-form';

type TUseVehicleModalFormProps = {
  isOpened: boolean;
};

const useVehicleModalForm = ({ isOpened }: TUseVehicleModalFormProps) => {
  const dispatch = useAppDispatch();

  const { dicts } = useAppSelector((state) => state.dict);
  const { userInfo } = useAppSelector((state) => state.userInfo);
  const { selectedBank, isEuBankOfflineProcess } = useAppSelector(
    (state) => state.financingStep
  );
  const { tsModalValues } = useAppSelector((state) => state.newOrder);

  const { requestId } = useAutoCredRequestId();

  const methods = useForm<VehicleFormInterface>({
    mode: selectedBank?.status.code === 'financed' ? 'onSubmit' : 'all'
  });
  const values = useWatch({ control: methods.control });

  const [storedValues, setStoredValues] =
    useState<Partial<VehicleFormInterface> | null>(null);

  const visibleFields = useMemo(() => {
    return getVisibleVehicleInfoFields({
      bankName: selectedBank?.bank.name as BankNames,
      isEuBankOfflineProcess
    });
  }, [selectedBank?.bank.name, isEuBankOfflineProcess]);

  const storageKey = useMemo(() => {
    if (!userInfo || !requestId) return '';
    return getVehicleFormKey(userInfo.uuid, requestId);
  }, [userInfo, requestId]);

  const setLocalStorage = useMemo(
    () =>
      debounce((storageKey: string, data: string) => {
        sessionStorage.setItem(storageKey, data);
      }, 500),
    []
  );

  const applyStoredValues = () => {
    if (!storageKey) return;
    const currentValues = methods.getValues();
    const mergedValues: VehicleFormInterface = {
      ...currentValues,
      ...storedValues
    };
    methods.reset(mergedValues);
    sessionStorage.removeItem(storageKey);
    setStoredValues(null);
  };

  const removeLocalStorage = () => {
    if (!storageKey) return;
    sessionStorage.removeItem(storageKey);
    setStoredValues(null);
  };

  const hasDifferenceBetweenStoredValues = useCallback(
    (
      stored: Partial<VehicleFormInterface>,
      current: VehicleFormInterface
    ): boolean => {
      return visibleFields.some((key) => {
        if (stored[key] === undefined) return false;
        const storedValue = String(stored[key]).trim();
        const currentValue = String(current[key]).trim();
        return storedValue !== currentValue;
      });
    },
    [visibleFields]
  );

  useEffect(() => {
    if (!storageKey) return;
    const filteredValues = filterVehicleFieldsByVisibility(
      values,
      visibleFields
    );
    const hasData = visibleFields.some((field) => filteredValues[field]);
    if (hasData) {
      setLocalStorage(storageKey, JSON.stringify(filteredValues));
    } else {
      sessionStorage.removeItem(storageKey);
      setStoredValues(null);
    }
  }, [values, storageKey, setLocalStorage, visibleFields]);

  useEffect(() => {
    if (!dicts || !storageKey || !isOpened) return;

    const tsModalFormValues = getVehicleInfoFormValues({
      tsModalValues,
      dicts,
      options: {
        bankName: selectedBank?.bank.name as BankNames,
        isEuBankOfflineProcess
      }
    });

    const storedData = sessionStorage.getItem(storageKey);

    if (storedData) {
      try {
        const parsedStoredData = JSON.parse(storedData) as VehicleFormInterface;
        const filteredStoredData = filterVehicleFieldsByVisibility(
          parsedStoredData,
          visibleFields
        );
        const filteredTsModalValues = filterVehicleFieldsByVisibility(
          tsModalFormValues,
          visibleFields
        );

        if (
          hasDifferenceBetweenStoredValues(
            filteredStoredData as VehicleFormInterface,
            filteredTsModalValues as VehicleFormInterface
          )
        ) {
          setStoredValues(filteredStoredData);
        }
      } catch (error) {
        handleError(error, {
          notify: false,
          customTitle: 'Ошибка парсинга данных из sessionStorage'
        });
      }
    }

    methods.reset(tsModalFormValues);
  }, [
    dicts,
    hasDifferenceBetweenStoredValues,
    isOpened,
    methods,
    storageKey,
    tsModalValues,
    visibleFields
  ]);

  useEffect(() => {
    if (!dicts) return;

    const tsModalFormValues = getVehicleInfoFormValues({
      tsModalValues,
      dicts,
      options: {
        bankName: selectedBank?.bank.name as BankNames,
        isEuBankOfflineProcess
      }
    });

    const isFormValuesValid =
      Object.values(tsModalFormValues).filter((item) => !item).length === 0;

    dispatch(setIsVehicleInfoValid(isFormValuesValid));
  }, [tsModalValues, dicts, selectedBank?.bank.name, isEuBankOfflineProcess]);

  return {
    applyStoredValues,
    compareValues: hasDifferenceBetweenStoredValues,
    getVisibleFields: () => visibleFields,
    methods,
    removeLocalStorage,
    storageKey,
    storedValues
  } as const;
};

export default useVehicleModalForm;
